import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import PostCard from '../components/PostCard';
import PostModal from '../components/PostModal';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { Post } from '../components/HomePage';
import './SearchPage.css';

const SearchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);

  useEffect(() => {
    const searchPosts = async () => {
      if (!query) {
        setPosts([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const headers: HeadersInit = {};
        if (currentUser) {
          const token = await currentUser.getIdToken();
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_ENDPOINTS.SEARCH}?q=${encodeURIComponent(query)}`, {
          headers
        });
        if (!response.ok) {
          throw new Error('Failed to fetch search results');
        }
        const data = await response.json();
        setPosts(data);
      } catch (err) {
        setError('Failed to load search results');
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    };

    searchPosts();
  }, [query, currentUser]);

  const handleCardClick = (post: Post) => {
    setSelectedPost(post);
  };

  const handleCloseModal = () => {
    setSelectedPost(null);
  };

  const handleYum = async (postId: string) => {
    if (!currentUser) return;

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_ENDPOINTS.YUM_POST}/${postId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to yum post');
      }

      // Update the post's yum status in the local state
      setPosts(posts.map(post => 
        post.id === postId 
          ? { 
              ...post, 
              is_yummed: !post.is_yummed,
              yum_count: post.is_yummed ? post.yum_count - 1 : post.yum_count + 1
            }
          : post
      ));
    } catch (err) {
      console.error('Error yumming post:', err);
    }
  };

  if (loading && posts.length === 0) {
    return (
      <div className="search-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading search results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="search-page">
      <h1 className="search-title">Search Results for "{query}"</h1>
      
      {error && (
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      )}

      {!loading && posts.length === 0 ? (
        <div className="no-posts-container">
          <p>No results found for "{query}"</p>
        </div>
      ) : (
        <>
          {loading && posts.length > 0 && (
            <div className="loading-overlay">
              <div className="loading-spinner small"></div>
            </div>
          )}
          <div className="posts-masonry">
            {posts.map(post => (
              <PostCard
                key={post.id}
                post={post}
                onCardClick={() => handleCardClick(post)}
                onYumClick={handleYum}
              />
            ))}
          </div>
        </>
      )}

      {selectedPost && (
        <PostModal
          post={selectedPost}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default SearchPage; 